import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { getCartManager } from '@theme/cart-manager';

/**
 * Carte de campagne de don (métaobjet `don_campagne`), rendue à l'identique sur
 * la fiche produit et dans le panier par `snippets/ax-don-campagne.liquid`.
 *
 * Le bouton ajoute la variante du don, puis reste désactivé sur « Ajouté » : le
 * retrait se fait depuis la ligne de panier. Toutes les instances de la page se
 * resynchronisent sur `cart:update`, y compris celles qui ne sont pas re-rendues
 * côté serveur (la fiche produit ne l'est pas).
 *
 * Seuil de panier : `data-minimum-amount` en centimes, 0 = aucune condition.
 * En dessous du seuil la carte est masquée, et un don déjà ajouté est retiré —
 * l'offre étant conditionnée au montant du panier, elle ne peut pas y survivre.
 *
 * @typedef {Object} AxDonCampagneRefs
 * @property {HTMLButtonElement} button - Le bouton « Faire un don ».
 *
 * @extends {Component<AxDonCampagneRefs>}
 */
class AxDonCampagne extends Component {
  requiredRefs = ['button'];

  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Cas du retour sur le site avec un panier repassé sous le seuil entre deux
    // visites : le rendu serveur sait déjà que la carte est masquée et que le don
    // est au panier, on n'attend donc pas une mise à jour pour le retirer.
    if (this.hidden && this.isInCart) this.#autoRemove();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  get minimumAmount() {
    return Number(this.dataset.minimumAmount) || 0;
  }

  get isInCart() {
    return this.dataset.inCart === 'true';
  }

  get useQuickAdd() {
    return this.dataset.useQuickAdd === 'true';
  }

  /** La ligne de panier du don, identifiée par sa propriété et non par sa variante. */
  #donLine(cart) {
    return cart?.items?.find((item) => item.properties?._is_don === 'true') ?? null;
  }

  /**
   * Ajoute le don. Le retrait ne passe PAS par ce bouton : une fois le don au panier,
   * il reste désactivé sur « Ajouté », et c'est la ligne de panier qui permet de le
   * retirer. Un bouton qui change de sens au clic — ajouter puis retirer — se déclenchait
   * par mégarde sur un geste de confirmation.
   */
  async toggle() {
    if (this.#busy || this.isInCart) return;

    if (this.useQuickAdd) {
      await this.#openQuickAdd();
      return;
    }

    this.#busy = true;
    this.refs.button.disabled = true;
    this.refs.button.dataset.state = 'loading';

    try {
      this.#clearError();
      const cart = await this.#add();
      this.#applyCart(cart);
      document.dispatchEvent(this.#cartUpdateEvent(cart));
    } catch (error) {
      // Rupture de stock, limite de quantité… : le client doit voir pourquoi
      // son clic n'a rien donné, pas seulement la console.
      this.#showError(error.message);
    } finally {
      this.#busy = false;
      delete this.refs.button.dataset.state;
      // Le don ajouté, le bouton reste désactivé : il n'a plus d'action à offrir.
      this.refs.button.disabled = this.isInCart;
    }
  }

  /**
   * Ouvre la pop-in d'ajout rapide et marque son formulaire comme un don.
   *
   * L'injection a lieu APRÈS `handleClick` : en mode différé le contenu du dialog
   * est fetché, donc le formulaire n'existe pas avant que la promesse soit tenue.
   * Il n'est pas reconstruit au changement de taille (seul l'input `id` est
   * réécrit), la propriété survit donc à la sélection — vérifié le 2026-10-09.
   */
  async #openQuickAdd() {
    const quickAdd = this.querySelector('quick-add-component');
    if (!quickAdd) return;

    this.#clearError();
    this.refs.button.dataset.state = 'loading';

    try {
      await quickAdd.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));

      const form = document
        .getElementById('quick-add-modal-content')
        ?.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) throw new Error("La fenêtre de choix n'a pas pu s'ouvrir.");

      if (!form.querySelector('input[name="properties[_is_don]"]')) {
        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'properties[_is_don]';
        flag.value = 'true';
        form.appendChild(flag);
      }
    } catch (error) {
      this.#showError(error.message);
    } finally {
      delete this.refs.button.dataset.state;
    }
  }

  /**
   * Event de mise à jour du panier destiné au reste du thème.
   *
   * `itemCount` est OBLIGATOIRE : le compteur du header (`cart-icon.js`) lit
   * `detail.data.itemCount` et retombe sur 0 en son absence — la pastille passait
   * donc à 0 puis se masquait à chaque ajout/retrait de don, alors que le panier
   * n'était pas vide.
   *
   * @param {{ item_count?: number }} cart - Le panier complet renvoyé par Shopify.
   * @returns {CartUpdateEvent}
   */
  #cartUpdateEvent(cart) {
    return new CartUpdateEvent(cart, 'ax-don-campagne', {
      source: 'ax-don-campagne',
      itemCount: cart?.item_count ?? 0,
    });
  }

  /**
   * `sections` dans le body signale au cart-manager une requête du thème : il
   * rafraîchit `cartJSON` sans dispatcher d'event, et c'est nous qui émettons
   * le nôtre — sinon l'event partirait en double. La clé doit être omise si on
   * n'a pas d'identifiant : Shopify répond 400 sur une chaîne vide.
   * @returns {Object} Le fragment de body à fusionner.
   */
  get #sectionsPayload() {
    const section = document.querySelector('cart-drawer-component')?.closest('.shopify-section');
    if (!section?.id) return {};

    return { sections: section.id.replace('shopify-section-', '') };
  }

  #showError(message) {
    const { error } = this.refs;
    if (!error) return;

    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const { error } = this.refs;
    if (!error) return;

    error.textContent = '';
    error.hidden = true;
  }

  async #add() {
    const body = JSON.stringify({
      items: [{ id: this.variantId, quantity: 1, properties: { _is_don: 'true' } }],
      ...this.#sectionsPayload,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.description || payload?.message || `Ajout impossible (HTTP ${response.status})`);
    }

    return getCartManager().getCart();
  }

  async #remove() {
    // `id` accepte la clé de ligne : c'est la seule façon de viser la bonne ligne
    // quand la même variante peut exister au panier avec et sans la propriété.
    // Le panier est chargé si besoin : au `connectedCallback` d'un retour sous le
    // seuil, `cartJSON` peut être vide, et `data-variant-id` ne vaut alors rien —
    // depuis la pop-in, la taille ajoutée n'est pas celle rendue par le serveur.
    const cart = getCartManager().cartJSON ?? (await getCartManager().getCart());
    const line = this.#donLine(cart);
    const body = JSON.stringify({
      id: String(line?.key ?? this.variantId),
      quantity: 0,
      ...this.#sectionsPayload,
    });
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.description || payload?.message || `Retrait impossible (HTTP ${response.status})`);
    }

    return getCartManager().getCart();
  }

  /**
   * Resynchronise toutes les instances quand le panier change ailleurs.
   * On lit `cartJSON` et non `event.detail.resource` : ce dernier peut être
   * partiel selon l'émetteur (cf. `.claude/rules/sidecart.md`).
   */
  #onCartUpdate = () => {
    if (this.#busy) return;

    this.#applyCart(getCartManager().cartJSON);
  };

  /**
   * Aligne l'affichage sur l'état réel du panier : présence du don, seuil, et
   * retrait automatique si le panier est repassé sous le seuil.
   * @param {Object} cart - Le panier renvoyé par Shopify.
   */
  #applyCart(cart) {
    if (!cart?.items) return;

    const inCart = this.#donLine(cart) !== null;
    const belowMinimum = this.minimumAmount > 0 && cart.total_price < this.minimumAmount;

    this.dataset.inCart = String(inCart);
    this.hidden = belowMinimum;

    const { button } = this.refs;
    const label = this.refs.buttonLabel ?? button;
    label.textContent = inCart ? button.dataset.labelAdded : button.dataset.labelAdd;
    button.classList.toggle('ax-don__button--added', inCart);
    button.disabled = inCart;

    if (belowMinimum && inCart) this.#autoRemove();
  }

  /** Le panier est repassé sous le seuil : le don n'est plus justifié. */
  async #autoRemove() {
    // Jamais de suppression automatique dans l'éditeur : on ne vide pas le
    // panier de test du marchand pendant qu'il règle sa section.
    if (this.#busy || Shopify.designMode) return;

    this.#busy = true;
    try {
      const cart = await this.#remove();
      this.dataset.inCart = 'false';
      document.dispatchEvent(this.#cartUpdateEvent(cart));
    } catch (error) {
      console.error('Campagne de don :', error);
    } finally {
      this.#busy = false;
      delete this.refs.button.dataset.state;
    }
  }
}

if (!customElements.get('ax-don-campagne')) {
  customElements.define('ax-don-campagne', AxDonCampagne);
}
